<ng-container *transloco="let t">
    <div cdkScrollable class="flex flex-col min-w-0 overflow-y-auto mb-18 lg:mb-4">
        <div class="flex flex-wrap w-full sm:p-6 max-w-screen-xl mx-auto">
            <fuse-alert class="mt-2 mb-4 w-full" *ngIf="alert.message" [appearance]="'fill'" [showIcon]="true"
                [type]="alert.type" [@shake]="alert.type === 'error'">
                {{alert.message | transloco}}
            </fuse-alert>

            <form [formGroup]="releaseForm" class="w-full sm:p-4 bg-card shadow overflow-hidden sm:rounded-2xl">
                <mat-vertical-stepper #verticalStepper (selectionChange)="onStepperSelectionChange($event)"
                    [selectedIndex]="currentStep" [linear]="true">
                    <ng-template matStepperIcon="edit">
                        <mat-icon>check</mat-icon>
                    </ng-template>
                    <mat-step #verticalStepperStep1 [formGroupName]="'metadata'"
                        [completed]="releaseForm.get('metadata').valid || releaseForm.disabled" [state]="'done'"
                        [stepControl]="releaseForm.get('metadata')">
                        <ng-template matStepLabel>{{t('release-form-step-metadata')}}</ng-template>

                        <div class="flex flex-col">
                            <mat-label class="pointer-events-none font-medium mb-1">Artwork *</mat-label>
                            <div class="flex flex-col md:flex-row mb-4">
                                <app-file-upload styling="w-64 h-64" [formControlName]="'artwork'" fileType="artwork"
                                    text="Upload Artwork *" [square]="true" [minWidth]="1000" [minHeight]="1000"
                                    [forUser]="release.user" [maxWidth]="4000" [maxHeight]="4000"></app-file-upload>
                                <div class="flex flex-col ml-4 pt-4 md:pt-0">
                                    <h2 class="font-semibold">{{'artwork-requirements' | transloco}}:</h2>
                                    <ul class="list-disc p-4">
                                        <li>{{'file-formats' | transloco}}: JPG, PNG</li>
                                        <li>{{t('artwork-resolution-limit-text', {minResolution: "1000x1000",
                                            maxResolution: "4000x4000"})}}</li>
                                        <li>{{'image-quality-requirement-text' | transloco}}</li>
                                        <li>{{'image-square-requirement-text' | transloco}}</li>
                                        <li>{{'image-no-logo-requirement-text' | transloco}}</li>
                                        <li>{{'image-rights-requirement-text' | transloco}}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div class="grid grid-cols-12 gap-2 mb-6">
                            <mat-form-field class="col-span-12 md:col-span-8">
                                <mat-label>{{'title' | transloco}}</mat-label>
                                <input [formControlName]="'title'" autocomplete="off" matInput required>
                            </mat-form-field>
                            <mat-form-field class="col-span-12 md:col-span-4">
                                <mat-label>{{"release-version" | transloco}} <form-field-tooltip
                                        tooltipTitleKey="release-version" tooltipKey="release-version-field-tooltip">
                                    </form-field-tooltip>
                                </mat-label>
                                <input [formControlName]="'version'" autocomplete="off" matInput>
                            </mat-form-field>
                            <mat-form-field appearance="fill" class="col-span-12 lg:col-span-6">
                                <mat-label>{{"main-artists" | transloco}} <form-field-tooltip
                                        tooltipTitleKey="main-artists"
                                        tooltipKey="release-primary-artists-field-tooltip"></form-field-tooltip>
                                </mat-label>
                                <mat-chip-grid #artistChipList aria-label="Artists" class="p-1" required>
                                    <mat-chip (removed)="removeArtist(artist, 'artists')"
                                        *ngFor="let artist of releaseForm.get('metadata').get('artists').value;"
                                        [removable]="true">
                                        {{artist.title}}
                                        <mat-icon matChipRemove>cancel</mat-icon>
                                    </mat-chip>
                                    <input #artistInput (input)="filterArtists($event)" (focus)="filterArtists($event)"
                                        (keydown)="filterArtistsInputKeyDown($event, 'artists')"
                                        [matAutocomplete]="artistAutoComplete" [matChipInputAddOnBlur]="false"
                                        (focusout)="onArtistBlur($event, 'artists')" [matChipInputFor]="artistChipList"
                                        placeholder="Add artist...">
                                </mat-chip-grid>
                                <mat-autocomplete #artistAutoComplete="matAutocomplete"
                                    (optionSelected)="addArtistFromAutocomplete($event, 'artists')">
                                    <mat-option *ngFor="let artist of filteredArtists" [value]="artist"
                                        (pointerdown)="autocompleteMouseDown($event)">
                                        {{artist.title}}
                                    </mat-option>
                                </mat-autocomplete>
                            </mat-form-field>
                            <mat-form-field appearance="fill" class="col-span-12 lg:col-span-6">
                                <mat-label>{{"feat-artists" | transloco}}</mat-label>
                                <mat-chip-grid #featArtistChipList aria-label="Featured Artists" class="p-1">
                                    <mat-chip (removed)="removeArtist(featArtist, 'featArtists')"
                                        *ngFor="let featArtist of releaseForm.get('metadata').get('featArtists').value;"
                                        [removable]="true">
                                        {{featArtist.title}}
                                        <mat-icon matChipRemove>cancel</mat-icon>
                                    </mat-chip>
                                    <input #featArtistInput (focus)="filterArtists($event)"
                                        (input)="filterArtists($event)" (focusout)="onArtistBlur($event, 'featArtists')"
                                        (keydown)="filterArtistsInputKeyDown($event, 'featArtists')"
                                        [matAutocomplete]="featArtistAutoComplete" [matChipInputAddOnBlur]="false"
                                        [matChipInputFor]="featArtistChipList" placeholder="Add featured artist...">
                                </mat-chip-grid>
                                <mat-autocomplete #featArtistAutoComplete="matAutocomplete"
                                    (optionSelected)="addArtistFromAutocomplete($event, 'featArtists')">
                                    <mat-option *ngFor="let artist of filteredArtists" [value]="artist"
                                        (pointerdown)="autocompleteMouseDown($event)">
                                        {{artist.title}}
                                    </mat-option>
                                </mat-autocomplete>
                            </mat-form-field>
                            <mat-form-field class="col-span-12 md:col-span-6 lg:col-span-3">
                                <mat-label>{{"genre" | transloco}}</mat-label>
                                <mat-select [formControlName]="'primaryGenre'" [compareWith]="compareByUuid" required>
                                    <ng-container *ngFor="let genre of genres; trackBy: trackByUuid">
                                        <mat-option [value]="genre">
                                            {{genre.title}}
                                        </mat-option>
                                    </ng-container>
                                </mat-select>
                            </mat-form-field>
                            <mat-form-field class="col-span-12 md:col-span-6 lg:col-span-3">
                                <mat-label>{{"subgenre" | transloco}}</mat-label>
                                <mat-select [formControlName]="'subGenre'" [compareWith]="compareByUuid">
                                    <mat-option [value]="null"></mat-option>
                                    <ng-container *ngFor="let genre of genres; trackBy: trackByUuid">
                                        <mat-option [value]="genre">
                                            {{genre.title}}
                                        </mat-option>
                                    </ng-container>
                                </mat-select>
                            </mat-form-field>
                            <mat-form-field class="col-span-12 md:col-span-6 lg:col-span-3">
                                <mat-label>{{"metadata-language" | transloco}} <form-field-tooltip
                                        tooltipTitleKey="metadata-language"
                                        tooltipKey="release-metadata-language-field-tooltip">
                                    </form-field-tooltip>
                                </mat-label>
                                <mat-select [formControlName]="'language'" [compareWith]="compareByUuid" required>
                                    <ng-container *ngFor="let language of languages">
                                        <mat-option [value]="language">
                                            {{t("lang." + language.alpha3)}}
                                        </mat-option>
                                    </ng-container>
                                </mat-select>
                            </mat-form-field>
                            <mat-form-field class="col-span-12 md:col-span-6 lg:col-span-3">
                                <mat-label>{{"audio-language" | transloco}} <form-field-tooltip
                                        tooltipTitleKey="audio-language"
                                        tooltipKey="release-audio-language-field-tooltip">
                                    </form-field-tooltip>
                                </mat-label>
                                <mat-select [formControlName]="'audioLanguage'" [compareWith]="compareByUuid" required>
                                    <mat-option [value]="'0'">{{"no-linguistic-content" | transloco}}</mat-option>
                                    <ng-container *ngFor="let language of languages">
                                        <mat-option [value]="language">
                                            {{t("lang." + language.alpha3)}}
                                        </mat-option>
                                    </ng-container>
                                </mat-select>
                            </mat-form-field>
                            <mat-form-field class="col-span-12 md:col-span-6 lg:col-span-4">
                                <mat-label>Label</mat-label>
                                <input [formControlName]="'label'" [matAutocomplete]="labelAutoComplete" matInput
                                    required>
                                <mat-autocomplete #labelAutoComplete="matAutocomplete"
                                    [displayWith]="getLabelName.bind(this)">
                                    <mat-option *ngFor="let label of filteredLabels$ | async" [value]="label">
                                        <ng-container *ngIf="!anyLabelHasLogo(); else labelDropdownWithLogo">
                                            {{label.title}}
                                        </ng-container>
                                        <ng-template #labelDropdownWithLogo>
                                            <div class="flex flex-row py-1 items-center">
                                                <div
                                                    class="relative flex flex-0 items-center justify-center w-10 h-10 mr-4 rounded">
                                                    <img *ngIf="label.logo" [alt]="'Logo'"
                                                        [src]="platformService.getFileSrc(label.logo, 'logo')"
                                                        class="w-full h-full object-contain">
                                                    <ng-container *ngIf="!label.logo">
                                                        <div
                                                            class="flex items-center justify-center w-full h-full rounded-full text-lg uppercase bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-200">
                                                            {{label?.title?.charAt(0) ?? "?"}}
                                                        </div>
                                                    </ng-container>
                                                </div>
                                                <span>{{label.title}}</span>
                                            </div>
                                        </ng-template>
                                    </mat-option>
                                </mat-autocomplete>
                            </mat-form-field>
                            <mat-form-field class="col-span-12 md:col-span-6 lg:col-span-3">
                                <mat-label>{{"catalog-number" | transloco}} <form-field-tooltip
                                        tooltipTitleKey="catalog-number" tooltipKey="release-catalog-id-field-tooltip">
                                    </form-field-tooltip></mat-label>
                                <input [formControlName]="'catalogId'" autocomplete="off" matInput>
                            </mat-form-field>

                            <mat-form-field class="col-span-12 md:col-span-6 lg:col-span-3">
                                <mat-label>{{"release-date" | transloco}} <form-field-tooltip
                                        tooltipTitleKey="release-date"
                                        tooltipKey="release-release-date-field-tooltip"></form-field-tooltip>
                                </mat-label>
                                <input [formControlName]="'releaseDate'" autocomplete="off" matInput type="date"
                                    required>
                                <mat-error
                                    *ngIf="releaseForm.get('metadata.releaseDate').hasError('pastReleaseDate')">The
                                    release date cannot be in the past</mat-error>
                                <mat-error
                                    *ngIf="releaseForm.get('metadata.releaseDate').hasError('releaseDateTooEarly')">The release date needs to be at least 5 days from now</mat-error>
                                <mat-hint
                                    *ngIf="requiresExpressDelivery() && expressDeliveryService && !releaseForm.get('metadata.releaseDate').hasError('pastReleaseDate')"
                                    class="text-yellow-700">Express delivery costs <b>{{expressDeliveryService.price |
                                        currency:platform.currency}}</b> extra
                                    <br>
                                    Earliest free release date: {{getEarliestFreeReleaseDate() | date: 'mediumDate'}}
                                </mat-hint>
                            </mat-form-field>


                            <mat-form-field class="col-span-12 md:col-span-6 lg:col-span-2">
                                <mat-label>{{"pre-order-date" | transloco}}</mat-label>
                                <input [formControlName]="'preorderDate'" autocomplete="off" matInput type="date">
                            </mat-form-field>
                        </div>

                        <mat-accordion id="description-accordion" class="w-full mb-4 mt-2"
                            style="filter: drop-shadow(0px 0px 0.5px #888);">
                            <mat-expansion-panel [expanded]="releaseForm.get('metadata.description')?.value?.length"
                                class="track-summary-panel rounded-md">
                                <mat-expansion-panel-header class="h-12 px-6 py-4">
                                    <mat-panel-title class="justify-start">
                                        <mat-icon [svgIcon]="'heroicons_solid:arrow-long-right'"
                                            class="mr-2 icon-size-5"></mat-icon>
                                        {{"release-description" | transloco}}
                                    </mat-panel-title>
                                </mat-expansion-panel-header>
                                <div class="grid grid-cols-12 gap-1 mt-2">
                                    <mat-form-field class="col-span-12">
                                        <textarea [formControlName]="'description'" matInput rows="5"
                                            maxlength="1000"></textarea>
                                        <mat-hint align="end"
                                            [style]="(releaseForm.get('metadata.description')?.value?.length ?? 0) >= 800 ? 'color: var(--fuse-warn) !important; font-weight: 500;' : ''">{{releaseForm.get('metadata.description')?.value?.length
                                            ?? 0}} / 1000</mat-hint>
                                    </mat-form-field>
                                </div>
                            </mat-expansion-panel>
                        </mat-accordion>

                        <div class="flex flex-col lg:flex-row mt-8 w-full">
                            <div class="flex flex-col mb-2 mr-4 pb-4">
                                <mat-label for="previously-published-radio-group"
                                    class="mat-mdc-floating-label mb-2 pointer-events-none relative" style="top: -5px; font-weight: 500;
                                    --tw-text-opacity: 1 !important;
                                    color: rgba(var(--fuse-text-default-rgb), var(--tw-text-opacity)) !important;">
                                    {{'previously-released-question' | transloco}}
                                    <form-field-tooltip tooltipTitleKey="previously-released-title" class="ml-2"
                                        tooltipKey="release-previously-released-tooltip">
                                    </form-field-tooltip>
                                </mat-label>
                                <mat-radio-group matInput class="flex flex-row" color="primary"
                                    formControlName="previouslyPublished">
                                    <mat-radio-button [value]="false">{{'no' | transloco}}</mat-radio-button>
                                    <mat-radio-button [value]="true" class="ml-8">{{'yes' |
                                        transloco}}</mat-radio-button>
                                </mat-radio-group>
                            </div>
                            <div class="grid grid-cols-12 gap-2 col-span-12 lg:col-span-8 flex-auto"
                                *ngIf="releaseForm.get('metadata.previouslyPublished').value">
                                <mat-form-field class="col-span-12 md:col-span-6">
                                    <mat-label>{{"upc-or-ean-code" | transloco}} <form-field-tooltip
                                            tooltipTitleKey="upc-or-ean-code" tooltipKey="ean-upc-field-tooltip">
                                        </form-field-tooltip>
                                    </mat-label>
                                    <input [formControlName]="'ean'" autocomplete="off" matInput required>
                                    <mat-error>{{'incorrect-release-code' | transloco}}</mat-error>
                                </mat-form-field>
                                <mat-form-field class="col-span-12 md:col-span-6">
                                    <mat-label>{{"original-release-date" | transloco}} <form-field-tooltip
                                            tooltipTitleKey="original-release-date"
                                            tooltipKey="release-original-release-date-field-tooltip">
                                        </form-field-tooltip>
                                    </mat-label>
                                    <input [formControlName]="'originalReleaseDate'" autocomplete="off" matInput
                                        type="date">
                                </mat-form-field>
                            </div>
                        </div>

                        <h2 class="font-semibold text-xl mt-4 mb-4">{{'copyright' | transloco}}</h2>
                        <div class="grid grid-cols-12 gap-2">
                            <mat-form-field class="col-span-8">
                                <mat-label>&copy; {{"c-line" | transloco}} <form-field-tooltip tooltipTitleKey="c-line"
                                        tooltipKey="release-c-line-field-tooltip">
                                    </form-field-tooltip>
                                </mat-label>
                                <input [formControlName]="'copyrightText'" [matAutocomplete]="cLineAutoComplete"
                                    matInput>
                                <mat-autocomplete #cLineAutoComplete="matAutocomplete">
                                    <mat-option
                                        *ngFor="let label of filterLabels(releaseForm.get('metadata.copyrightText').value)"
                                        [value]="label.title">
                                        {{label.title}}
                                    </mat-option>
                                </mat-autocomplete>
                            </mat-form-field>
                            <mat-form-field class="col-span-4">
                                <mat-label>&copy; {{"year" | transloco}}</mat-label>
                                <mat-select [formControlName]="'copyrightYear'">
                                    <ng-container *ngFor="let year of copyrightYears">
                                        <mat-option [value]="year">
                                            {{year}}
                                        </mat-option>
                                    </ng-container>
                                </mat-select>
                            </mat-form-field>
                            <mat-form-field class="col-span-8">
                                <mat-label>℗ {{"p-line" | transloco}} <form-field-tooltip tooltipTitleKey="p-line"
                                        tooltipKey="release-p-line-field-tooltip">
                                    </form-field-tooltip>
                                </mat-label>
                                <input [formControlName]="'pText'" [matAutocomplete]="pLineAutoComplete" matInput>
                                <mat-autocomplete #pLineAutoComplete="matAutocomplete">
                                    <mat-option
                                        *ngFor="let label of filterLabels(releaseForm.get('metadata.pText').value)"
                                        [value]="label.title">
                                        {{label.title}}
                                    </mat-option>
                                </mat-autocomplete>
                            </mat-form-field>
                            <mat-form-field class="col-span-4">
                                <mat-label>℗ {{"year" | transloco}}</mat-label>
                                <mat-select [formControlName]="'pYear'">
                                    <ng-container *ngFor="let year of copyrightYears">
                                        <mat-option [value]="year">
                                            {{year}}
                                        </mat-option>
                                    </ng-container>
                                </mat-select>
                            </mat-form-field>
                        </div>
                        <div class="flex justify-end">
                            <button [color]="'primary'" class="px-8" mat-flat-button matStepperNext type="button">
                                {{t('next-button')}}
                            </button>
                        </div>
                    </mat-step>

                    <mat-step #verticalStepperStep2
                        [completed]="trackListValidator()(undefined) === null || releaseForm.disabled"
                        [formGroupName]="'tracks'" [stepControl]="releaseForm.get('tracks')">
                        <ng-template matStepLabel>{{t('release-form-step-tracks')}}</ng-template>

                        <div (cdkDropListDropped)="trackDropped($event)" [cdkDropListData]="release?.tracks" cdkDropList
                            class="divide-y mt-4">

                            <!-- Track -->
                            <ng-container *ngFor="let track of release?.tracks; let i = index;">
                                <div [cdkDragLockAxis]="'y'" [id]="'track-' + track.uuid"
                                    [cdkDragDisabled]="releaseForm.disabled" cdkDrag
                                    class="group w-full select-none hover:bg-gray-100 dark:hover:bg-hover h-16">

                                    <!-- Drag preview -->
                                    <div *cdkDragPreview class="flex flex-0 w-0 h-0"></div>

                                    <!-- List content -->
                                    <div class="track relative flex items-center h-full pl-10">
                                        <!-- Drag handle -->
                                        <div cdkDragHandle
                                            class="md:hidden absolute flex items-center justify-center inset-y-0 left-0 w-8 cursor-move md:group-hover:flex">
                                            <mat-icon [svgIcon]="'heroicons_solid:bars-3'"
                                                class="icon-size-5 text-hint">
                                            </mat-icon>
                                        </div>

                                        <!-- Track link -->
                                        <div class="track-link flex flex-auto items-center min-w-0 h-full md:pr-7">
                                            <!-- Track number -->
                                            <div class="mat-step-header mr-3">
                                                <div class="mat-step-icon mat-step-icon-state-number"
                                                    [ngClass]="{'mat-step-icon-selected': validateTrack(track)}">
                                                    <div class="mat-step-icon-content" ng-reflect-ng-switch="false">
                                                        <span aria-hidden="true" class="ng-star-inserted">{{i +
                                                            1}}.</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- <div class="hidden sm:inline-flex justify-end mr-3">
                                                <span [ngClass]="{'completed': validateTrack(track)}"
                                                    class="track-number">{{i + 1}}.</span>
                                            </div> -->
                                            <!-- Title & Placeholder -->
                                            <div class="truncate text-lg">
                                                <ng-container *ngIf="track.title">
                                                    <span>{{track.title}}</span><span
                                                        *ngIf="track.version">&nbsp;({{track.version}})</span>
                                                </ng-container>
                                                <ng-container *ngIf="!track.title">
                                                    <span class="select-none text-hint">{{t('untitled-track')}}</span>
                                                </ng-container>
                                            </div>

                                        </div>
                                        <div class="button-bar ml-auto justify-self-end space-x-3 mr-3">
                                            <mat-icon (click)="playTrack(track)" *ngIf="track.audioFile"
                                                [svgIcon]="'heroicons_solid:play'"
                                                class="inline-flex icon-size-7 cursor-pointer"
                                                matTooltip="{{t('play-track-tooltip')}}"></mat-icon>
                                            <mat-icon [ngClass]="{'hidden': releaseForm.disabled}"
                                                (click)="openTrackModal(track)" [svgIcon]="'heroicons_solid:pencil'"
                                                [ngClass]="{'pulse-animation': !validateTrack(track)}"
                                                class="icon-size-7 cursor-pointer"
                                                matTooltip="{{t('edit-track-details-tooltip')}}"></mat-icon>
                                            <mat-icon [ngClass]="{'hidden': releaseForm.disabled}"
                                                (click)="openDeletionConfirmationModal(track, i)"
                                                [svgIcon]="'heroicons_solid:trash'" class="icon-size-7 cursor-pointer"
                                                matTooltip="{{t('delete-track-tooltip')}}"></mat-icon>
                                        </div>
                                        <!-- Duration -->
                                        <div class="hidden justify-self-end justify-end md:flex min-w-10">
                                            <span *ngIf="track.audioFile?.duration">{{track.audioFile.duration |
                                                duration}}</span>
                                        </div>

                                    </div>
                                    <!--<div class="track-file-info absolute bottom-0 left-18 text-sm text-hint">Codec: {{track.codec}} at {{track.bitrateKbps | number}} kbps | Sampling rate: {{track.samplingRate/1000 | number}} kHz | Bit depth: {{track.bitDepth}} bit</div>-->
                                    <div *ngIf="track.audioFile?.codec"
                                        class="track-file-info hidden opacity-0 sm:flex absolute bottom-0.5 left-10 sm:left-18 text-sm text-hint">
                                        Format: {{getFormatString(track)}}
                                    </div>
                                </div>
                            </ng-container>
                        </div>

                        <div class="flex flex-col w-full justify-center mt-4 mb-4">
                            <div class="w-full h-18 rounded overflow-hidden">
                                <app-file-upload *ngIf="!releaseForm.disabled" [formControl]="newTrackInput"
                                    [forUser]="release.user" [parentUuid]="release.uuid" fileType="track"
                                    text="Upload Track">
                                </app-file-upload>
                            </div>
                            <div class="flex flex-col ml-4 pt-4 md:pt-0">
                                <h2 class="font-semibold">{{'track-requirements' | transloco}}:</h2>
                                <ul class="list-disc p-4 py-2">
                                    <li>WAV (16-bit &#64; 44.1kHz)</li>
                                    <li>FLAC (16-bit &#64; 44.1kHz)</li>
                                    <li>HD WAV (24-bit &#64; 44.1, 48, 88.2, 96, 192kHz)</li>
                                    <li>HD FLAC (24-bit &#64; 44.1, 48, 88.2, 96, 192kHz)</li>
                                    <li>{{'reencode-warning-text' | transloco}}</li>
                                </ul>
                            </div>
                        </div>
                        <div class="flex justify-end">
                            <button [color]="'accent'" class="px-8 mr-2" mat-flat-button matStepperPrevious
                                type="button">
                                {{t('back-button')}}
                            </button>
                            <button [color]="'primary'"
                                [matTooltip]="verticalStepperStep2.stepControl.invalid ? ('validation-error-incomplete-tracks' | transloco) : ''"
                                class="px-8" mat-flat-button matStepperNext type="button">
                                {{t('next-button')}}
                            </button>
                        </div>
                    </mat-step>

                    <mat-step #verticalStepperStep3
                        [completed]="releaseForm.get('storesStep').valid || releaseForm.disabled"
                        [formGroupName]="'storesStep'" [stepControl]="releaseForm.get('storesStep')">
                        <ng-template matStepLabel>{{t('release-form-step-stores')}}</ng-template>

                        <div class="flex flex-col mt-2 mb-4 mr-4">
                            <mat-form-field class="fuse-mat-no-subscript">
                                <mat-label for="geoRestrictionStyle">
                                    {{'geographical-restrictions' | transloco}}
                                </mat-label>
                                <mat-select formControlName="geoRestrictionStyle">
                                    <mat-option [value]="'none'">{{'no-geo-restrictions' | transloco}}</mat-option>
                                    <mat-option [value]="'whitelist'">{{'geo-whitelist-option' |
                                        transloco}}</mat-option>
                                    <mat-option [value]="'blacklist'">{{'geo-blacklist-option' |
                                        transloco}}</mat-option>
                                </mat-select>
                            </mat-form-field>
                        </div>

                        <div *ngIf="releaseForm.get('storesStep.geoRestrictionStyle').value === 'whitelist'"
                            class="mb-2 sm:my-2 font-medium">
                            <p class="inline-block mt-auto mb-auto">
                                {{t('release-form-allowed-countries-description')}}
                            </p>
                            <app-country-selector [emptyChipLabel]="t('worldwide')" formControlName="countryWhitelist">
                            </app-country-selector>
                        </div>

                        <div *ngIf="releaseForm.get('storesStep.geoRestrictionStyle').value === 'blacklist'"
                            class="mb-2 sm:my-2 font-medium">
                            <p class="inline-block mt-auto mb-auto">
                                {{t('release-form-disallowed-countries-description')}}
                            </p>
                            <app-country-selector [emptyChipLabel]="t('none')" formControlName="countryBlacklist">
                            </app-country-selector>
                        </div>

                        <div class="mb-2 sm:mb-4 mt-10 font-medium">
                            <mat-label for="blacklist-whitelist-radio-group"
                                class="mat-mdc-floating-label mb-2 pointer-events-none relative"
                                style="top: -5px; font-weight: 500;
                                --tw-text-opacity: 1 !important;
                                color: rgba(var(--fuse-text-default-rgb), var(--tw-text-opacity)) !important;">{{t('release-form-stores-description')}}
                            </mat-label>
                        </div>
                        <div class="flex flex-col gt-sm:flex-row">
                            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                <mat-checkbox id="allStoresCheckbox" #allStoresCheckbox
                                    (change)="allStoresChange(allStoresCheckbox.checked)" [color]="'primary'"
                                    value="false" [disabled]="releaseForm.disabled">
                                    {{t('check-all-checkbox-label')}}
                                </mat-checkbox>
                                <!-- <mat-checkbox #futureStoresCheckbox
                                    (change)="futureStoresChange(futureStoresCheckbox.checked)" [color]="'primary'"
                                    [formControlName]="'publishAtFutureStores'">
                                    {{t('publish-at-future-stores')}}
                                </mat-checkbox> -->
                            </div>
                            <div class="border-b my-4 border-gray-300"></div>
                            <div [formGroupName]="'stores'"
                                class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                <div *ngFor="let store of stores;">
                                    <mat-checkbox [color]="'primary'" [formControlName]="store.uuid"
                                        [disabled]="releaseForm.disabled || isStoreDisallowed(store)">
                                        <div class="flex items-center">
                                            <div *ngIf="store.logo"
                                                class="inline-flex flex-0 items-center justify-center w-6 h-6 overflow-hidden mr-2">
                                                <img [src]="platformService.getFileSrc(store.logo, 'logo')"
                                                    alt="Store image" class="object-contain w-full h-full" />
                                            </div>
                                            {{store.title}}
                                        </div>
                                    </mat-checkbox>
                                </div>
                            </div>
                        </div>
                        <div class="flex justify-end mt-8">
                            <button [color]="'accent'" class="px-8 mr-2" mat-flat-button matStepperPrevious
                                type="button">
                                {{t('back-button')}}
                            </button>
                            <button [color]="'primary'" [disabled]="verticalStepperStep3.stepControl.invalid"
                                class="px-8" mat-flat-button matStepperNext type="button">
                                {{t('next-button')}}
                            </button>
                        </div>
                    </mat-step>

                    <mat-step #verticalStepperStep4 id="reviewStep"
                        [completed]="releaseForm.valid || releaseForm.disabled" [formGroupName]="'reviewStep'"
                        [stepControl]="releaseForm.get('reviewStep')">
                        <ng-template matStepLabel>{{t('release-form-step-review')}}</ng-template>
                        <div class="flex flex-col md:flex-row p-1 md:p-4">
                            <div class="flex flex-col items-center md:items-start mb-4 mt-4 md:mt-0">
                                <div class="flex flex-col items-center">
                                    <div class="w-64 h-64 rounded overflow-hidden">
                                        <img *ngIf="release?.artwork" [alt]="'Release artwork'"
                                            [src]="platformService.getFileSrc(release?.artwork, 'artwork')"
                                            class="col-span-3 w-64 h-64 object-contain">
                                    </div>
                                </div>
                            </div>

                            <div class="flex flex-auto flex-wrap md:ml-6 mb-4">

                                <div class="w-full md:gap-6">
                                    <div class="text-3xl font-medium">{{releaseForm.get('metadata').get('title').value}}
                                    </div>
                                    <span
                                        class="text-xl">{{getArtistNames(releaseForm.get('metadata').get('artists').value)}}</span>
                                    <span *ngIf="releaseForm.get('metadata').get('featArtists').value.length > 0"
                                        class="text-xl">&nbsp;feat.
                                        {{getArtistNames(releaseForm.get('metadata').get('featArtists').value)}}
                                    </span>
                                    <div class="grid mt-4 leading-6">
                                        <div class="grid summary-grid">
                                            <div class="colon-after">Label</div>
                                            <div>{{getLabelName(releaseForm.get('metadata.label').value)}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{"metadata-language" | transloco}}</div>
                                            <div *ngIf="releaseForm.get('metadata.language').value; else noLanguage">
                                                {{"lang." +
                                                releaseForm.get('metadata.language').value.alpha3 | transloco}}</div>
                                            <ng-template #noLanguage>
                                                <div>Not set</div>
                                            </ng-template>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{"audio-language" | transloco}}</div>
                                            <div
                                                *ngIf="releaseForm.get('metadata.audioLanguage').value && releaseForm.get('metadata.audioLanguage').value !== '0'; else noAudioLanguage">
                                                {{"lang." +
                                                releaseForm.get('metadata.audioLanguage').value.alpha3 | transloco}}
                                            </div>
                                            <ng-template #noAudioLanguage>
                                                <div>{{"no-linguistic-content" | transloco}}</div>
                                            </ng-template>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{"genre" | transloco}}</div>
                                            <div>{{getGenreNames(releaseForm.get('metadata.primaryGenre').value,
                                                releaseForm.get('metadata.subGenre').value)}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{"release-date" | transloco}}</div>
                                            <div>{{releaseForm.get('metadata.releaseDate').value | date}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{"upc-or-ean-code" | transloco}}</div>
                                            <div
                                                [ngClass]="{'text-hint': releaseForm.get('metadata.ean').value?.length === 0}">
                                                {{releaseForm.get('metadata.ean').value?.length > 0 ?
                                                releaseForm.get('metadata.ean').value :
                                                t('ean-will-be-assigned')}}
                                            </div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">Copyright</div>
                                            <div>&copy; {{releaseForm.get('metadata.copyrightYear').value}}
                                                {{releaseForm.get('metadata.copyrightText').value}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">Stores</div>
                                            <div>{{getStoreNames()}}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="text-xl w-full p-2 mb-2 border-gray-300">{{t('number-of-tracks', {tracks:
                            release?.tracks?.length})}}</div>
                        <mat-accordion id="track-summary-accordion">
                            <mat-expansion-panel *ngFor="let track of release?.tracks; let i = index;"
                                [expanded]="release?.tracks.length === 1" class="track-summary-panel rounded-md">
                                <mat-expansion-panel-header class="h-12 px-6 py-4">
                                    <mat-panel-title>
                                        {{i + 1}}. {{track.title}}<span
                                            *ngIf="track.version">&nbsp;({{track.version}})</span>
                                    </mat-panel-title>
                                </mat-expansion-panel-header>
                                <div class="grid grid-cols-1 md:grid-cols-2">
                                    <div class="grid leading-6">
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{"artist-optional-plural" | transloco}}</div>
                                            <div>{{getTrackArtistNames(track.participants)}}</div>
                                        </div>
                                        <div *ngIf="getOtherTrackParticipants(track.participants) !== ''"
                                            class="grid summary-grid">
                                            <div class="colon-after">{{'participant-optional-plural' | transloco}}</div>
                                            <div>{{getOtherTrackParticipants(track.participants)}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{'genre-optional-plural' | transloco}}</div>
                                            <div>{{getGenreNames(track.primaryGenre, track.subGenre)}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{'language' | transloco}}</div>
                                            <div
                                                *ngIf="track.language && track.language !== '0'; else noTrackLanguage;">
                                                {{"lang." + track.language?.alpha3 | transloco}}</div>
                                            <ng-template #noTrackLanguage>
                                                <div>{{'no-linguistic-content' | transloco}}</div>
                                            </ng-template>
                                        </div>
                                        <div *ngIf="track.composer" class="grid summary-grid">
                                            <div class="colon-after">{{'composer' | transloco}}</div>
                                            <div>{{track.composer}}</div>
                                        </div>
                                        <div *ngIf="track.songwriter" class="grid summary-grid">
                                            <div class="colon-after">{{'songwriter' | transloco}}</div>
                                            <div>{{track.songwriter}}</div>
                                        </div>
                                        <div *ngIf="track.isrc" class="grid summary-grid">
                                            <div class="colon-after">{{'isrc' | transloco}}</div>
                                            <div>{{track.isrc}}</div>
                                        </div>
                                        <div *ngIf="track.iswc" class="grid summary-grid">
                                            <div class="colon-after">{{'iswc' | transloco}}</div>
                                            <div>{{track.iswc}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{'explicit' | transloco}}</div>
                                            <div>{{track.explicit ? t('yes') : t('no')}}</div>
                                        </div>
                                    </div>
                                    <div class="grid leading-6">
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{'duration' | transloco}}</div>
                                            <div>{{track.audioFile?.duration | duration}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{'format' | transloco}}</div>
                                            <div>{{track.audioFile?.codec ? track.audioFile.codec : t('unknown')}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{'bitrate' | transloco}}</div>
                                            <div>{{track.audioFile?.bitRate / 1000 | number: '1.0-1'}} {{'kbps' |
                                                transloco}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{'sample-rate' | transloco}}</div>
                                            <div>{{track.audioFile?.samplingRate / 1000 | number: '1.0-1'}} {{'khz' |
                                                transloco}}</div>
                                        </div>
                                        <div class="grid summary-grid">
                                            <div class="colon-after">{{'bit-depth' | transloco}}</div>
                                            <div>{{track.audioFile?.lossless ? track.audioFile.bitDepth + "-" +
                                                t("bit-depth-bit") :
                                                t("bit-depth-not-applicable")}}</div>
                                        </div>
                                    </div>
                                </div>
                            </mat-expansion-panel>
                        </mat-accordion>

                        <fuse-alert class="mb-4 mt-4"
                            *ngIf="releaseForm?.get('reviewStep')?.errors?.singleTitleMismatch" [appearance]="'outline'"
                            [showIcon]="false" [type]="'error'">
                            <div [innerHTML]="t('single-title-mismatch')"></div>
                        </fuse-alert>

                        <fuse-alert class="mb-4 mt-4"
                            *ngIf="releaseForm?.get('reviewStep')?.errors?.singleVersionMismatch"
                            [appearance]="'outline'" [showIcon]="false" [type]="'error'">
                            <div [innerHTML]="t('single-version-mismatch')"></div>
                        </fuse-alert>

                        <fuse-alert class="mb-4 mt-4"
                            *ngIf="releaseForm?.get('reviewStep')?.errors?.singleGenreMismatch" [appearance]="'outline'"
                            [showIcon]="false" [type]="'error'">
                            <div [innerHTML]="t('single-genre-mismatch')"></div>
                        </fuse-alert>

                        <fuse-alert class="mb-4 mt-4"
                            *ngIf="releaseForm?.get('reviewStep')?.errors?.singleArtistMismatch"
                            [appearance]="'outline'" [showIcon]="false" [type]="'error'">
                            <div [innerHTML]="t('single-artist-mismatch')"></div>
                        </fuse-alert>

                        <fuse-alert class="mb-4 mt-4"
                            *ngIf="releaseForm?.get('reviewStep')?.errors?.noComposer"
                            [appearance]="'outline'" [showIcon]="false" [type]="'error'">
                            <div [innerHTML]="t('error-missing-composer')"></div>
                        </fuse-alert>

                        <fuse-alert class="mb-4 mt-4"
                            *ngIf="releaseForm?.get('reviewStep')?.errors?.duplicateTracks"
                            [appearance]="'outline'" [showIcon]="false" [type]="'error'">
                            <div [innerHTML]="t('error-duplicate-track-title')"></div>
                        </fuse-alert>

                        <ng-container *ngIf="user.role == 'user'">
                            <div class="text-xl mt-10">
                                {{'subscription' | transloco}}
                            </div>
                            <div *ngIf="(usableSubscriptions$ | async)?.length" class="text-hint mt-2">
                                {{'subscription-selection-description' | transloco}}
                            </div>
                            <div *ngIf="!(usableSubscriptions$ | async)?.length" class="text-warn-600 mt-2">
                                {{'subscription-selection-none-available-description' | transloco}}
                            </div>
                            <div class="flex flex-col md:flex-row mt-2 space-x-2">
                                <mat-form-field class="flex-auto w-full text-sm sm:text-base">
                                    <mat-select [formControlName]="'subscription'" [compareWith]="compareByUuid"
                                        required>
                                        <ng-container *ngFor="let subscription of usableSubscriptions$ | async">
                                            <mat-option [value]="subscription">
                                                {{t('subscription-plan-title', {title: subscription.title})}}
                                                ({{subscription.releases.length + "/" + subscription.maxReleases + " " +
                                                t('releases').toLowerCase()}})
                                            </mat-option>
                                        </ng-container>
                                    </mat-select>
                                </mat-form-field>
                                <button [disabled]="releaseForm.disabled" (click)="goToSubscriptions()"
                                    [ngClass]="{'pulse-animation': !(usableSubscriptions$ | async)?.length}"
                                    [color]="'primary'" class="px-8" mat-flat-button type="button">
                                    <mat-icon [svgIcon]="'heroicons_outline:plus'"></mat-icon>
                                    <span class="ml-2 mr-1">{{t('add-new-subscription')}}</span>
                                </button>
                            </div>
                        </ng-container>

                        <div class="flex justify-end mt-8">
                            <button [color]="'accent'" class="px-8 mr-2" mat-flat-button matStepperPrevious
                                type="button">
                                {{t('back-button')}}
                            </button>
                            <button [disabled]="releaseForm.disabled" (click)="submitForm('/releases', 'draft')"
                                [color]="'accent'" class="px-8 mr-2" mat-flat-button type="submit">
                                <mat-icon [svgIcon]="'heroicons_solid:inbox-arrow-down'"></mat-icon>
                                <span *ngIf="release.status === 'draft'"
                                    class="ml-2 mr-1">{{t('save-draft-button')}}</span>
                                <span *ngIf="release.status !== 'draft'"
                                    class="ml-2 mr-1">{{t('update-release-button')}}</span>
                            </button>
                            <button *ngIf="release.status !== 'pending-approval'"
                                [disabled]="!releaseForm.valid || verticalStepperStep4.stepControl.invalid || releaseForm.disabled"
                                (click)="submitForm('/releases', 'publish')" [color]="'primary'" class="px-8"
                                mat-raised-button type="submit">
                                <mat-icon [svgIcon]="'heroicons_solid:globe-alt'"></mat-icon>
                                <span class="ml-2 mr-1">{{t('publish-release-button')}}</span>
                            </button>
                        </div>
                    </mat-step>
                </mat-vertical-stepper>
            </form>
        </div>
    </div>
</ng-container>
